import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useImportUsers } from '@/hooks/use-admin'
import type { BulkImportResponse } from '@/services/admin.service'
import { downloadImportTemplate } from '@/services/admin.service'
import {
  ArrowLeft,
  CheckCircle,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  XCircle,
} from 'lucide-react'
import type React from 'react'
import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

export function AdminImportPage() {
  const [file, setFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [result, setResult] = useState<BulkImportResponse | null>(null)

  const importUsers = useImportUsers()

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files?.[0]) {
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile)
        setResult(null)
      }
    }
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0])
      setResult(null)
    }
  }

  const handleImport = async () => {
    if (!file) return

    try {
      const response = await importUsers.mutateAsync(file)
      setResult(response)
    } catch (error) {
      console.error('Import failed:', error)
    }
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/users">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Import Users</h1>
          <p className="text-muted-foreground">Bulk import users from a CSV file</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle>Upload CSV</CardTitle>
            <CardDescription>
              Upload a CSV file with user data. Required columns: phone_number or email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Dropzone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm font-medium">
                  {file ? file.name : 'Drop your CSV file here or click to browse'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">CSV files only, max 10MB</p>
              </label>
            </div>

            {/* Selected File */}
            {file && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFile(null)
                    setResult(null)
                  }}
                >
                  Remove
                </Button>
              </div>
            )}

            {/* Import Button */}
            <Button
              onClick={handleImport}
              disabled={!file || importUsers.isPending}
              className="w-full"
            >
              {importUsers.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Users
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Format Guide */}
        <Card>
          <CardHeader>
            <CardTitle>CSV Format</CardTitle>
            <CardDescription>Your CSV should include these columns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" onClick={downloadImportTemplate} className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Download CSV Template
            </Button>

            <div className="rounded-lg bg-muted p-4 font-mono text-xs overflow-x-auto">
              <p className="text-muted-foreground">
                first_name,last_name,email,phone_number,dob,gender,address,organization
              </p>
              <p>John,Doe,john@example.com,+15551234567,1990-01-15,male,123 Main St,Acme Corp</p>
              <p>Jane,Smith,jane@example.com,,1985-06-20,female,456 Oak Ave,Tech Inc</p>
              <p>Bob,Johnson,,+15559876543,,,,</p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Column Requirements:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  <strong>first_name</strong>, <strong>last_name</strong> - Required
                </li>
                <li>
                  <strong>email</strong> or <strong>phone_number</strong> - At least one is required
                </li>
                <li>
                  <strong>dob</strong> - Optional, date of birth (YYYY-MM-DD format)
                </li>
                <li>
                  <strong>gender</strong> - Optional (male, female, other, prefer_not_to_say)
                </li>
                <li>
                  <strong>address</strong> - Optional, full address
                </li>
                <li>
                  <strong>organization</strong> - Optional, organization name (auto-created if new)
                </li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Notes:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>Phone numbers should be in E.164 format (+1234567890)</li>
                <li>Duplicate emails/phones will be skipped</li>
                <li>Auth method is auto-detected based on provided fields</li>
                <li>Organizations are auto-created if they don't exist</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
            <CardDescription>
              {result.success_count} of {result.total} users imported successfully
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">{result.success_count} Successful</span>
              </div>
              {result.failure_count > 0 && (
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" />
                  <span className="font-medium">{result.failure_count} Failed</span>
                </div>
              )}
            </div>

            {result.failure_count > 0 && (
              <div className="rounded-lg border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="h-10 px-4 text-left text-sm font-medium">Row</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Status</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results
                      .filter((r) => !r.success)
                      .map((r) => (
                        <tr key={r.row} className="border-b">
                          <td className="p-4 text-sm">{r.row}</td>
                          <td className="p-4">
                            <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-red-100 text-red-700">
                              Failed
                            </span>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">{r.error}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
